import { AdType, RtbCampaignPlanType } from 'core/rtbCampaign/RtbCampaign';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { channelScopedKey } from 'core/agency/AddonFeature';
import { isOneForAllCreativeType } from 'core/creative/Creative';
import { getI18nString } from 'utils/I18nUtils';

/**
 * Agency add-on 開關之間的層級關係：
 *   channel group → ad type → creative(banner) type
 *                          → pricing model (per-channel) → bid-price optimize row
 *
 * 父層關閉時，子層開關不顯示，且其值會被 normalize 為 false（出價設定的
 * `addonProps.campaignBidPrice[*].bidFloor` 例外：純畫面隱藏，不清資料）。
 *
 * 計價模式自此次起改為「per-channel flat composite key」結構：
 *   addonFeatures.campaign[`channel_${channelGroupId}_${addonKey}`] = boolean
 * 與 `addonFeatures.campaign.<key>`（root，全域旗標）同層共存。經
 * `DefaultAddonFeatureManager.convertAgencyAddonFeatures` 攤平時，
 * `channel_*` flat key 會加上 `campaign_` 前綴變成
 * `campaign_channel_${id}_${addonKey}` 進入 `modelAddonFeatures`，方便
 * `isFeatureEnable(perChannelCampaignKey(...))` 比對。
 * （OtherSettings 等 — 待後續 task per-channel 化）。
 */

type AddonFeatures = any;

type AdTypeSettingLike = {
  adTypeId?: number;
  name: string;
  pricingModel?: string[];
};

export const adTypeOptionKey = (
  adTypeSetting: AdTypeSettingLike
): string | number => adTypeSetting.adTypeId ?? adTypeSetting.name;

/**
 * RtbCampaignPlanType (BE `DynamicCampaignAdTypeSetting.pricingModel[]` 的值)
 * → addonFeatures.campaign.channel[channelGroupId].<key>（Agency 計價模式開關）
 *
 * fbcpm 不在 Agency 開關清單中，所以不映射；它不會顯示在 UI 上。
 */
const PRICING_MODEL_TO_ADDON_KEY: Partial<Record<RtbCampaignPlanType, string>> =
  {
    [RtbCampaignPlanType.RS]: 'revenueSharing',
    [RtbCampaignPlanType.FCPC]: 'fix_cpc',
    [RtbCampaignPlanType.FCPV]: 'fix_cpv',
    [RtbCampaignPlanType.FCPM]: 'fix_cpm',
    [RtbCampaignPlanType.FVCPM]: 'fix_vcpm',
    [RtbCampaignPlanType.FACPM]: 'fix_acpm',
    [RtbCampaignPlanType.FWCPM]: 'fix_wcpm',
    [RtbCampaignPlanType.DCPM]: 'dynamic_cpm'
  };

/**
 * `setting.autoBidCap` / `setting.bidFloor` 的 optimize key（lowercase）
 * → addonFeatures.campaign.channel[channelGroupId].<key>（Agency 計價模式開關）
 *
 * BidPriceSettings 分頁用：僅做畫面 row 隱藏，不動 form state。
 */
const OPTIMIZE_TO_ADDON_KEY: Record<string, string> = {
  cpc: 'fix_cpc',
  cpm: 'fix_cpm',
  cpv: 'fix_cpv',
  vcpm: 'fix_vcpm',
  acpm: 'fix_acpm',
  wcpm: 'fix_wcpm',
  dcpm: 'dynamic_cpm'
};

/** Agency PricingModel tab 上會出現的 8 個開關 key，依介面順序。 */
export const PRICING_MODEL_ADDON_KEYS = [
  'fix_cpc',
  'fix_cpm',
  'fix_cpv',
  'dynamic_cpm',
  'fix_vcpm',
  'fix_acpm',
  'fix_wcpm',
  'revenueSharing'
] as const;

/**
 * `addonFeatures.${category}.channel_${channelGroupId}_${addonKey}` 的 Formik
 * 路徑（用於 FormikField.name）。資料以扁平 key 儲存於 `${category}` 根節點，
 * BE friendly；經 `DefaultAddonFeatureManager.convertAgencyAddonFeatures`
 * 攤平時會加上 `${category}_` 前綴，變成最終的
 * `${category}_channel_${id}_${key}` 供 `isFeatureEnable` 比對。
 */
export const channelBasedAddonFieldPath = (
  category: string,
  channelGroup: ChannelGroupSetting,
  addonKey: string
): string =>
  `addonFeatures.${category}.${channelScopedKey(channelGroup.config.id, addonKey)}`;

/**
 * channel group 的顯示名稱，與「投遞渠道」頁的開關 label 一致：
 * 優先取 i18n `agency.addonItem.channel.${addonName}`，否則 fallback config.name。
 */
export const getChannelGroupName = (
  channelGroup: ChannelGroupSetting
): string =>
  getI18nString(
    `agency.addonItem.channel.${channelGroup.config.addonName}`,
    channelGroup.config.name
  );

/**
 * ECOPD-5566：停用渠道一律視為「關閉」，不管 agency 的旗標實際是什麼。
 *
 * 兩個理由：
 *  1. 停用渠道從此仍會被註冊（見 `ChannelGroupWebService.getChannelGroups`），
 *     不再像過去那樣「因為沒被註冊所以自然不顯示」，必須顯式擋。
 *  2. 後端關閉 agency 旗標的批次若漏掉某個 agency，
 *     `addonFeatures.channel.<key>` 會留著 true —— 這裡要容忍那種髒資料。
 *
 * 因為 `getVisibleAdTypes` / `getVisiblePricingModes` / `normalizeAddonFeatures`
 * 都以它為入口，擋在這一層等於同時收斂廣告類型、素材類型、計價模式、出價四個分頁。
 */
export const isChannelEnabled = (
  addonFeatures: AddonFeatures,
  channelGroup: ChannelGroupSetting
): boolean =>
  !channelGroup.isEffectivelyArchived() &&
  !!addonFeatures?.channel?.[channelGroup.config.addonName];

export const isAdTypeEnabled = (
  addonFeatures: AddonFeatures,
  adTypeSetting: AdTypeSettingLike
): boolean =>
  !!addonFeatures?.adTypes?.[`adType_${adTypeOptionKey(adTypeSetting)}`];

/**
 * 某個 channel 下，指定計價模式 addon key 是否啟用。
 * 新結構（flat composite key）：
 *   addonFeatures.campaign[`channel_${channelGroupId}_${addonKey}`]
 */
export const isPricingModeEnabled = (
  addonFeatures: AddonFeatures,
  channelGroup: ChannelGroupSetting,
  addonKey: string
): boolean =>
  !!addonFeatures?.campaign?.[
    channelScopedKey(channelGroup.config.id, addonKey)
  ];

/** 任一 channel 啟用了指定計價模式 addon key（per-channel 結構） */
export const isAnyChannelPricingModeEnabled = (
  addonFeatures: AddonFeatures,
  addonKey: string
): boolean =>
  // ECOPD-5566: 停用渠道殘留的旗標不該讓全域計價模式開關看起來還開著
  ChannelGroupSetting.activeValues().some((channelGroup) =>
    isPricingModeEnabled(addonFeatures, channelGroup, addonKey)
  );

/** channel 開啟時可見的 active ad types（不含已下架的 One For All），channel 關閉則為空 */
export const getVisibleAdTypes = (
  addonFeatures: AddonFeatures,
  channelGroup: ChannelGroupSetting
) =>
  isChannelEnabled(addonFeatures, channelGroup)
    ? channelGroup
        .getActiveCampaignAdTypes()
        .filter(
          (adTypeSetting) => adTypeSetting.name !== AdType.ONE_FOR_ALL_DISPLAY
        )
    : [];

/**
 * channel 開 且 至少一個含它的父 ad type 開時，才可見的 banner types（去重，不含已下架的 One For All）；
 * 否則為空。
 */
export const getVisibleCreativeTypes = (
  addonFeatures: AddonFeatures,
  channelGroup: ChannelGroupSetting
) => {
  const enabledAdTypes = getVisibleAdTypes(addonFeatures, channelGroup).filter(
    (adTypeSetting) => isAdTypeEnabled(addonFeatures, adTypeSetting)
  );
  const seen = new Set<number>();
  return enabledAdTypes
    .flatMap((adTypeSetting) => adTypeSetting.bannerTypes)
    .filter((bannerType) => {
      if (
        seen.has(bannerType.bannerTypeId) ||
        isOneForAllCreativeType(bannerType.bannerTypeId)
      ) {
        return false;
      }
      seen.add(bannerType.bannerTypeId);
      return true;
    });
};

/**
 * 某個 channel 下，已啟用 ad type 共同支援的計價模式 addon key 集合（依
 * PRICING_MODEL_ADDON_KEYS 的順序）。channel 關 或 沒有任何 ad type 開時為空。
 */
export const getVisiblePricingModes = (
  addonFeatures: AddonFeatures,
  channelGroup: ChannelGroupSetting
): string[] => {
  const enabledAdTypes = getVisibleAdTypes(addonFeatures, channelGroup).filter(
    (adTypeSetting) => isAdTypeEnabled(addonFeatures, adTypeSetting)
  );
  const supported = new Set<string>();
  enabledAdTypes.forEach((adTypeSetting) => {
    (adTypeSetting.pricingModel ?? []).forEach((code) => {
      const addonKey = PRICING_MODEL_TO_ADDON_KEY[code as RtbCampaignPlanType];
      if (addonKey) supported.add(addonKey);
    });
  });
  return PRICING_MODEL_ADDON_KEYS.filter((k) => supported.has(k));
};

/**
 * 對 `setting.autoBidCap` / `setting.bidFloor` 的 optimize key list 過濾出
 * 指定 channel 下已啟用的計價模式對應項。Form state 不會被改動 — 純畫面用。
 */
export const getEnabledOptimizes = (
  addonFeatures: AddonFeatures,
  channelGroup: ChannelGroupSetting,
  optimizes: string[]
): string[] =>
  optimizes.filter((optimize) => {
    const addonKey = OPTIMIZE_TO_ADDON_KEY[optimize.toLowerCase()];
    return addonKey
      ? isPricingModeEnabled(addonFeatures, channelGroup, addonKey)
      : false;
  });

/**
 * 依目前父層（channel / ad type）狀態，把不可見的子開關值強制為 false，
 * 回傳新的 addonFeatures。在父層開關 onChange 時呼叫，確保送出的 payload 一致。
 *
 * 注意：
 * - `addonProps.campaignBidPrice[*].bidFloor / autoBidCap` 不在此處處理，
 *   計價模式關閉時純粹是 BidPriceSettingsForm 的渲染過濾，不會清資料。
 * - per-channel 計價模式以 flat composite key 儲存於 `addonFeatures.campaign`
 *   根節點（`channel_${channelGroupId}_${addonKey}`）；每個 channel 依其已啟用
 *   ad type 各自決定哪些 pricing model 為 true。`addonFeatures.campaign.<key>`
 *   （非 `channel_*` 的 root 旗標）的全域旗標不在此處被觸碰。
 */
export const normalizeAddonFeatures = (
  addonFeatures: AddonFeatures
): AddonFeatures => {
  const adTypes = { ...(addonFeatures?.adTypes ?? {}) };
  const creatives = { ...(addonFeatures?.creatives ?? {}) };
  const campaign = { ...(addonFeatures?.campaign ?? {}) };
  const campaignLimitation = { ...(addonFeatures?.campaignLimitation ?? {}) };
  const channel = { ...(addonFeatures?.channel ?? {}) };

  // ECOPD-5572：先算出「仍啟用的渠道擁有哪些 ad type / banner type」，再決定要清什麼。
  //
  // ⚠️ 這一步必須在任何寫入之前算完。ad type 的 key 是 `adTypeId ?? name`，
  // 所以靜態渠道之間（例如同名的 DISPLAY / VIDEO）**必然共用**，banner type
  // 也可能掛在多個渠道底下。逐渠道邊算邊清的話，先處理到的停用渠道會把仍啟用
  // 渠道的 key 一起清掉，結果取決於 registry 的遍歷順序 —— 症狀是經銷商莫名
  // 少了一個還開著的廣告類型，而且不會有任何錯誤。
  // ECOPD-5572：只把**已經存在**的 key 關掉，不替沒有的 key 生一個 false 出來。
  //
  // 這支現在會在經銷商編輯頁「載入時」與「存檔時」各跑一次，所以憑空生 key 的
  // 代價是每個經銷商、每次存檔都在 payload 裡多出幾十個從來不存在的欄位，
  // 而稽核紀錄不會過濾它們 —— 每一個都會變成一列「修改」。功能上無害
  // （攤平 add-on 時 falsy key 本來就被跳過），但雜訊是真的。
  const turnOff = (target: Record<string, any>, key: string) => {
    if (key in target) {
      target[key] = false;
    }
  };

  const enabledAdTypeKeys = new Set<string>();
  const enabledBannerTypeIds = new Set<number>();
  ChannelGroupSetting.values().forEach((channelGroup) => {
    if (!isChannelEnabled(addonFeatures, channelGroup)) {
      return;
    }
    channelGroup.getActiveCampaignAdTypes().forEach((adTypeSetting) => {
      const adTypeKey = `adType_${adTypeOptionKey(adTypeSetting)}`;
      enabledAdTypeKeys.add(adTypeKey);
      // banner type 還多一個條件：含它的那個 ad type 本身也要開著。
      if (adTypes[adTypeKey]) {
        adTypeSetting.bannerTypes.forEach((bannerType) =>
          enabledBannerTypeIds.add(bannerType.bannerTypeId)
        );
      }
    });
  });

  ChannelGroupSetting.values().forEach((channelGroup) => {
    // ECOPD-5566: 停用渠道自己那顆開關也要清掉。
    // 它的 UI 已經不再渲染（見 AgencySalesChannelAddonForm 改用
    // getActiveAddonConfigs），若後端關閉 agency 旗標的批次漏掉這個 agency，
    // 殘留的 true 就會沒有任何入口可以關、又在每次存檔時被整包 PUT 回去。
    if (channelGroup.isEffectivelyArchived()) {
      turnOff(channel, channelGroup.config.addonName);
    }
    const channelOn = isChannelEnabled(addonFeatures, channelGroup);
    const activeAdTypes = channelGroup.getActiveCampaignAdTypes();

    // channel 關 → 此 group 的 ad type 全部 false，
    // 但**不能連坐**仍被其他啟用渠道擁有的 key（見上方 enabledAdTypeKeys）。
    if (!channelOn) {
      activeAdTypes.forEach((adTypeSetting) => {
        const adTypeKey = `adType_${adTypeOptionKey(adTypeSetting)}`;
        if (!enabledAdTypeKeys.has(adTypeKey)) {
          turnOff(adTypes, adTypeKey);
        }
      });
    }

    // campaignLimitation（per-channel flat key `channel_${id}_${feature}`）：
    // 只掛在 channel 底下，不經過 ad type，所以 channel 關就整段清掉。
    //
    // ⚠️ 這一段的 UI 在 channel 關閉時**整個 section 不渲染**
    // （`AgencyCampaignTAAddonForm` 依 channel 開關決定要不要出 section），
    // 所以殘值既沒有入口可以關、又會在每次存檔時被整包 PUT 回後端。
    //
    // 只清「已經存在的 key」，不替沒有 section 的渠道生出新 key。
    if (!channelOn) {
      const scopePrefix = channelScopedKey(channelGroup.config.id, '');
      Object.keys(campaignLimitation)
        .filter((key) => key.startsWith(scopePrefix))
        .forEach((key) => {
          turnOff(campaignLimitation, key);
        });
    }

    // banner type：需**任一**啟用渠道底下有一個開著的 ad type 含它，否則 false。
    //
    // 覆蓋集合同樣是全域算好的。⚠️ 誠實說明：banner type 在本 repo 是**一個
    // bannerTypeId 只屬於一個渠道**（`ChannelGroupSettings.test.ts` 有一條全域
    // 守門測試），所以在那個前提成立時，全域集合與原本的 per-channel 集合等價，
    // 這一段是**防禦性的、無法在不違反該前提的情況下被測紅**。保留的理由是不想
    // 讓正確性建立在「另一個檔案的一條測試」上 —— 那條守門測試本身受執行順序
    // 影響，靠不住。
    activeAdTypes
      .flatMap((adTypeSetting) => adTypeSetting.bannerTypes)
      .forEach((bannerType) => {
        if (!enabledBannerTypeIds.has(bannerType.bannerTypeId)) {
          turnOff(creatives, `option_${bannerType.bannerTypeId}`);
        }
      });

    // pricing model（per-channel flat key）：此 channel 下需有至少一個已啟用
    // ad type 支援該 pricing model，才保留；否則
    // `campaign[channel_${channelGroupId}_${addonKey}]` = false。
    const supportedAddonKeys = new Set<string>();
    if (channelOn) {
      activeAdTypes.forEach((adTypeSetting) => {
        const adTypeOn = !!adTypes[`adType_${adTypeOptionKey(adTypeSetting)}`];
        if (!adTypeOn) return;
        (adTypeSetting.pricingModel ?? []).forEach((code) => {
          const addonKey =
            PRICING_MODEL_TO_ADDON_KEY[code as RtbCampaignPlanType];
          if (addonKey) supportedAddonKeys.add(addonKey);
        });
      });
    }
    PRICING_MODEL_ADDON_KEYS.forEach((addonKey) => {
      if (!supportedAddonKeys.has(addonKey)) {
        turnOff(campaign, channelScopedKey(channelGroup.config.id, addonKey));
      }
    });

    // enableOptimizedPrice：值本身不綁單一 ad type，但畫面上它跟著計價模式區塊走
    // （PricingModelForm 在該渠道沒有任何可用計價模式時整段不渲染），所以清除條件
    // 也一致 —— channel 關、或這個渠道一個計價模式都不支援時清為 false，免得留下
    // 使用者看不到也改不掉的 true。
    if (!channelOn || supportedAddonKeys.size === 0) {
      turnOff(
        campaign,
        channelScopedKey(channelGroup.config.id, 'enableOptimizedPrice')
      );
    }
  });

  // 再行銷追蹤（campaign root flag）依賴 COMBO 廣告類型 add-on；
  // COMBO 關閉（含 channel 關連動）時強制 false
  if (!adTypes[`adType_${AdType.COMBO}`]) {
    turnOff(campaign, 'retargeting_tracker');
  }

  // 影片廣告觀看目標（campaign root flag）依賴任一 channel 啟用 fix_cpv；
  // 都沒開（含 channel / ad type 連動關）時強制 false
  // ECOPD-5566: 同上，停用渠道的 fix_cpv 不算數
  const anyFixCpvEnabled = ChannelGroupSetting.activeValues().some(
    (channelGroup) =>
      !!campaign[channelScopedKey(channelGroup.config.id, 'fix_cpv')]
  );
  if (!anyFixCpvEnabled) {
    turnOff(campaign, 'videoAdViewObjective');
  }

  return {
    ...addonFeatures,
    channel,
    adTypes,
    creatives,
    campaign,
    campaignLimitation
  };
};
